/**
 * Procedural sound effects via WebAudio. No asset pipeline: each SFX is a
 * short oscillator + envelope crafted in code so the bundle stays tiny.
 *
 * Browsers gate AudioContext on a user gesture; we lazily construct it on
 * the first sfx() call so we don't get the "AudioContext was not allowed
 * to start" warning until something actually plays.
 */

// Sounds to play on combo (not on regular merge).
// Index 0 = low combo (x2–x3), index 1 = high combo (x4+).
const COMBO_CANDIDATES = [
  '/sfx/merge-nice-9.mp3',      // 1 low  — quick, punchy
  '/sfx/merge-sparkle-3.mp3',   // 2 high — longer, grander
];


// Saved for potential repurposing — not in rotation yet:
//   /sfx/merge-extra-8.mp3   (was merge #9  — slime splat)
//   /sfx/merge-extra-12.mp3  (was merge #13 — click pop soft)
let comboCandidateIndex = 0;
const comboCandidateBuffers: (AudioBuffer | null)[] = COMBO_CANDIDATES.map(() => null);
const comboCandidateLoaders: (Promise<void> | null)[] = COMBO_CANDIDATES.map(() => null);

// The merge sound. Playtesting picked the "blip" — the whomp/coin alternates
// and the in-settings picker were retired.
const MERGE_SFX_URL = '/sfx/merge-nice-8.mp3';
let mergeBuffer: AudioBuffer | null = null;
let mergeLoader: Promise<void> | null = null;

function loadComboCandidate(index: number): void {
  if (comboCandidateLoaders[index]) return;
  comboCandidateLoaders[index] = (async () => {
    try {
      const c = await runningCtx();
      if (!c) return;
      const res = await fetch(COMBO_CANDIDATES[index]);
      if (!res.ok && res.status !== 0) return;
      comboCandidateBuffers[index] = await c.decodeAudioData(await res.arrayBuffer());
    } catch {}
  })();
}

function playComboCandidate(gain = 0.55, comboLevel = 2): void {
  const c = ensureCtx();
  if (!c) return;
  // Low combo (x2–x3) → index 0, high combo (x4+) → index 1.
  const idx = comboLevel >= 4 ? 1 : 0;
  loadComboCandidate((idx + 1) % COMBO_CANDIDATES.length);
  const buf = comboCandidateBuffers[idx];
  if (!buf) { loadComboCandidate(idx); return; }
  const src = c.createBufferSource();
  src.buffer = buf;
  const env = c.createGain();
  env.gain.value = gain;
  src.connect(env);
  env.connect(c.destination);
  src.start();
}


type SfxName = 'drop' | 'merge' | 'combo' | 'gameOver' | 'tickHigh' | 'buy';

let ctx: AudioContext | null = null;
let muted = false;
const MUTE_KEY = 'shmerge.muted';

// Music state — single looping track.
let musicGain: GainNode | null = null;
let musicSource: AudioBufferSourceNode | null = null;
let musicBuffer: AudioBuffer | null = null;
let musicStarted = false;
let musicEnabled = true; // synced with the 'music' setting

// Decoded sample buffers for SFX that play a real audio file rather than a
// procedural tone. Loaded lazily on first ensureCtx() so we don't pre-create
// an AudioContext before any user gesture.
const sampleBuffers: Partial<Record<SfxName, AudioBuffer>> = {};
const sampleUrls: Partial<Record<SfxName, string>> = {
  drop: '/drop.mp3',
};
const sampleLoaders: Partial<Record<SfxName, Promise<void>>> = {};

function loadSample(name: SfxName, url: string): Promise<void> {
  if (sampleLoaders[name]) return sampleLoaders[name]!;
  sampleLoaders[name] = (async () => {
    try {
      const c = await runningCtx();
      if (!c) return;
      const res = await fetch(url);
      // status 0 = Capacitor custom-scheme URLResponse (not HTTPURLResponse) — body is valid
      if (!res.ok && res.status !== 0) return;
      const arr = await res.arrayBuffer();
      sampleBuffers[name] = await c.decodeAudioData(arr);
    } catch {
      // Fall through; the procedural fallback in sfx() will play instead.
    }
  })();
  return sampleLoaders[name]!;
}

function playSample(name: SfxName, gain = 0.5): boolean {
  const c = ensureCtx();
  if (!c) return false;
  const buf = sampleBuffers[name];
  if (!buf) {
    const url = sampleUrls[name];
    if (url) void loadSample(name, url);
    return false;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const env = c.createGain();
  env.gain.value = gain;
  src.connect(env);
  env.connect(c.destination);
  src.start();
  return true;
}

try {
  muted = localStorage.getItem(MUTE_KEY) === '1';
  const storedMusic = localStorage.getItem('shmerge.settings.music');
  if (storedMusic !== null) musicEnabled = storedMusic !== '0';
} catch {}

// Pause all audio when the app is backgrounded (Android/iOS home button, tab
// switch). Without this the music keeps playing with the screen off.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    if (document.hidden) {
      try { musicSource?.stop(); } catch {}
      musicSource = null;
      ctx.suspend().catch(() => {});
    } else {
      // Resume context and restart the music loop if it was active.
      ctx.resume().then(() => {
        if (musicStarted && musicEnabled && musicBuffer && ctx) {
          startMusicSource(ctx);
        }
      }).catch(() => {});
    }
  });
}

function loadMergeSound(): void {
  if (mergeLoader) return;
  mergeLoader = (async () => {
    try {
      const c = await runningCtx();
      if (!c) return;
      const res = await fetch(MERGE_SFX_URL);
      if (!res.ok && res.status !== 0) return;
      mergeBuffer = await c.decodeAudioData(await res.arrayBuffer());
    } catch {}
  })();
}

/** Returns the friendly name of the current combo sound. */
export function getComboLabel(): string {
  const idx = comboCandidateIndex % COMBO_CANDIDATES.length;
  const file = COMBO_CANDIDATES[idx].split('/').pop()!.replace('.mp3', '').replace('merge-', '');
  return `${idx + 1}/${COMBO_CANDIDATES.length} ${file}`;
}

/** Advance combo sound, play it, return new label. */
export function cycleComboSound(): string {
  comboCandidateIndex = (comboCandidateIndex + 1) % COMBO_CANDIDATES.length;
  loadComboCandidate(comboCandidateIndex);
  const buf = comboCandidateBuffers[comboCandidateIndex];
  const c = ensureCtx();
  if (c && buf) {
    const src = c.createBufferSource();
    src.buffer = buf;
    const env = c.createGain();
    env.gain.value = 0.55;
    src.connect(env);
    env.connect(c.destination);
    src.start();
  }
  return getComboLabel();
}

function playMergeSound(gain = 0.55): void {
  const c = ensureCtx();
  if (!c) return;
  const buf = mergeBuffer;
  if (!buf) { loadMergeSound(); return; }
  const src = c.createBufferSource();
  src.buffer = buf;
  const env = c.createGain();
  env.gain.value = gain;
  src.connect(env);
  env.connect(c.destination);
  src.start();
}

function ensureCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

// iOS WKWebView requires the AudioContext to be in 'running' state before
// decodeAudioData will resolve. This helper ensures that before returning.
async function runningCtx(): Promise<AudioContext | null> {
  const c = ensureCtx();
  if (!c) return null;
  try {
    if (c.state !== 'running') await c.resume();
  } catch {}
  return c;
}

export function setMuted(m: boolean): void {
  muted = m;
  try {
    localStorage.setItem(MUTE_KEY, m ? '1' : '0');
  } catch {}
}

export function isMuted(): boolean {
  return muted;
}

interface ToneOpts {
  freq: number;
  endFreq?: number;
  durationMs: number;
  type?: OscillatorType;
  gain?: number;
  delayMs?: number;
}

function tone({ freq, endFreq, durationMs, type = 'sine', gain = 0.18, delayMs = 0 }: ToneOpts): void {
  if (muted) return;
  const c = ensureCtx();
  if (!c) return;
  const start = c.currentTime + delayMs / 1000;
  const dur = durationMs / 1000;

  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (endFreq !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(endFreq, 0.0001), start + dur);
  }

  const env = c.createGain();
  env.gain.setValueAtTime(0, start);
  env.gain.linearRampToValueAtTime(gain, start + 0.005);
  env.gain.exponentialRampToValueAtTime(0.0001, start + dur);

  osc.connect(env);
  env.connect(c.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

function noiseBurst(durationMs: number, gain = 0.12): void {
  if (muted) return;
  const c = ensureCtx();
  if (!c) return;
  const samples = Math.floor((durationMs / 1000) * c.sampleRate);
  const buf = c.createBuffer(1, samples, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < samples; i++) {
    const t = i / samples;
    data[i] = (Math.random() * 2 - 1) * (1 - t);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const env = c.createGain();
  env.gain.value = gain;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 600;
  src.connect(filter);
  filter.connect(env);
  env.connect(c.destination);
  src.start();
}

export function sfx(name: SfxName, opts: { tier?: number } = {}): void {
  if (muted) return;
  switch (name) {
    case 'drop': {
      // Prefer the loaded MP3 ("pop") if it's decoded; fall back to the old
      // procedural square-wave thud if not (e.g. first frame before fetch).
      if (playSample('drop', 0.6)) return;
      tone({ freq: 180, endFreq: 70, durationMs: 110, type: 'square', gain: 0.08 });
      noiseBurst(60, 0.05);
      return;
    }
    case 'merge': {
      playMergeSound(0.55);
      return;
    }
    case 'combo': {
      playComboCandidate(0.55, opts.tier ?? 2);
      return;
    }
    case 'gameOver': {
      // Descending buzz.
      tone({ freq: 220, endFreq: 60, durationMs: 380, type: 'sawtooth', gain: 0.16 });
      tone({ freq: 110, endFreq: 30, durationMs: 380, type: 'sawtooth', gain: 0.10, delayMs: 60 });
      return;
    }
    case 'tickHigh': {
      tone({ freq: 1200, durationMs: 40, type: 'sine', gain: 0.12 });
      return;
    }
    case 'buy': {
      tone({ freq: 520, endFreq: 780, durationMs: 80, type: 'triangle', gain: 0.15 });
      tone({ freq: 780, endFreq: 1040, durationMs: 80, type: 'triangle', gain: 0.12, delayMs: 60 });
      return;
    }
  }
}

/**
 * Best-effort resume after a user gesture (iOS Safari & some Chrome flavors
 * leave the context in 'suspended' state until you touch). Call from
 * pointerup handlers.
 */
export function resumeAudio(): void {
  ensureCtx()?.resume().catch(() => {});
  for (const [name, url] of Object.entries(sampleUrls)) {
    if (url) void loadSample(name as SfxName, url);
  }
  // Preload the merge sound and all combo candidates eagerly.
  loadMergeSound();
  for (let i = 0; i < COMBO_CANDIDATES.length; i++) loadComboCandidate(i);
}

/**
 * Start the music source. The track is authored as a seamless loop, so native
 * AudioBufferSourceNode looping gives a sample-accurate transition — no
 * crossfade scheduling needed.
 */
function startMusicSource(c: AudioContext): void {
  if (!musicBuffer || !musicGain || !musicStarted || !musicEnabled) return;

  const src = c.createBufferSource();
  src.buffer = musicBuffer;
  src.loop = true;
  src.connect(musicGain);
  src.start();
  musicSource = src;
}

/** Load and start the background music loop. Safe to call multiple times. */
export async function startMusic(): Promise<void> {
  const c = ensureCtx();
  if (!c) return;
  if (musicStarted) {
    applyMusicGain();
    return;
  }
  musicStarted = true;

  if (!musicGain) {
    musicGain = c.createGain();
    musicGain.connect(c.destination);
  }
  applyMusicGain();

  if (!musicBuffer) {
    try {
      if (c.state !== 'running') await c.resume();
      // "Chill lofi inspired" by omfgdude, seamless loop edit by qubodup (CC0)
      // https://opengameart.org/content/chill-lofi-inspired-loop-edit
      const res = await fetch('/music-loop.ogg');
      // status 0 = Capacitor custom-scheme URLResponse (not HTTPURLResponse) — body is valid
      if (!res.ok && res.status !== 0) { musicStarted = false; return; }
      musicBuffer = await c.decodeAudioData(await res.arrayBuffer());
    } catch {
      musicStarted = false;
      return;
    }
  }

  startMusicSource(c);
}

/** Stop the music (e.g. on game over, or when setting is toggled off). */
export function stopMusic(): void {
  try { musicSource?.stop(); } catch {}
  musicSource = null;
  musicStarted = false;
}

/** Called by the settings service when the music toggle changes. */
export function setMusicEnabled(on: boolean): void {
  musicEnabled = on;
  applyMusicGain();
  if (on && !musicStarted) {
    void startMusic();
  }
}

function applyMusicGain(): void {
  if (!musicGain) return;
  musicGain.gain.value = musicEnabled ? 0.272 : 0;
}
